import React, { useEffect, useRef } from "react";
import * as am4core from "@amcharts/amcharts4/core";
import * as am4charts from "@amcharts/amcharts4/charts";
import am4themes_animated from "@amcharts/amcharts4/themes/animated";
import am4themes_dark from "@amcharts/amcharts4/themes/dark";
import * as XLSX from "xlsx";   // ← ADD



am4core.useTheme(am4themes_animated);

const GraphDisplay = ({ graphs, allBlocks, onDeleteGraph }) => {
    const safeGraphs = Array.isArray(graphs) ? graphs : [];
  
    return (
      <div style={{ display: "flex", flexWrap: "wrap", gap: "20px" }}>
        {safeGraphs.map((graph, index) => (
          <Graph
            key={index}
            index={index}
            data={graph}
            allBlocks={allBlocks}
            onDelete={() => onDeleteGraph?.(index)}
          />
        ))}
      </div>
    );
  };

export const Graph = ({ data, index, onDelete, allBlocks, headerContent = null }) => {
    const chartRef = useRef(null);

    useEffect(() => {
        if (!data || !data.graphData) return;

        am4core.useTheme(am4themes_dark);

        let chart;
        if (data.type === "bar") {
            chart = am4core.create(chartRef.current, am4charts.XYChart);
            chart.paddingRight = 20;

            // Create X Axis
            let categoryAxis = chart.xAxes.push(new am4charts.CategoryAxis());
            categoryAxis.dataFields.category = "key";
            categoryAxis.renderer.grid.template.location = 0;
            categoryAxis.renderer.minGridDistance = 30;
            if(data.keyAxisLabel){
                categoryAxis.title.text = data.keyAxisLabel; // Add X-axis label
            }

            // Create Y Axis
            let valueAxis = chart.yAxes.push(new am4charts.ValueAxis());
            if(data.valueAxisLabel){
                valueAxis.title.text = data.valueAxisLabel; // Add Y-axis label
            }

            // Create series
            let series = chart.series.push(new am4charts.ColumnSeries());
            series.dataFields.valueY = "value";
            series.dataFields.categoryX = "key";
            series.columns.template.tooltipText = "{key}: [bold]{value}[/]";
            series.columns.template.fillOpacity = 0.8;
            series.columns.template.strokeWidth = 2;
            series.columns.template.strokeOpacity = 1;

            series.columns.template.adapter.add("fill", (fill, target) => {
                return am4core.color(target.dataItem.dataContext.color);
            });

            chart.data = data.graphData;

        } else if (data.type === "pie" || data.type === "donut") {
            chart = am4core.create(chartRef.current, am4charts.PieChart);
            if (data.type === "donut") {
                chart.innerRadius = am4core.percent(60);
            }
            let pieSeries = chart.series.push(new am4charts.PieSeries());
            pieSeries.dataFields.value = "value";
            pieSeries.dataFields.category = "key";
            pieSeries.slices.template.tooltipText = "{key}: [bold]{value}[/]";
            pieSeries.slices.template.fillOpacity = 0.8;
            if (data.type === "donut") {
                pieSeries.labels.template.disabled = true;
                pieSeries.ticks.template.disabled = true;
            }

            pieSeries.slices.template.adapter.add("fill", (fill, target) => {
                return am4core.color(target.dataItem.dataContext.color);
            });

            chart.data = data.graphData;

            if (data.type === "donut") {
                const centerLabel = chart.seriesContainer.createChild(am4core.Label);
                centerLabel.text = data.centerLabel || "";
                centerLabel.horizontalCenter = "middle";
                centerLabel.verticalCenter = "middle";
                centerLabel.fontSize = 20;
                centerLabel.fill = am4core.color("#fff");
            }

        } else if (data.type === "funnel") {
            chart = am4core.create(chartRef.current, am4charts.SlicedChart);
            chart.paddingRight = 20;
            chart.data = data.graphData;
            let series = chart.series.push(new am4charts.FunnelSeries());
            series.dataFields.value = "value";
            series.dataFields.category = "key";
            series.slices.template.tooltipText = "{category}: [bold]{value}[/]";
            series.labels.template.fill = am4core.color("#fff");
            series.labels.template.adapter.add("text", (text, target) => {
                const dataItem = target.dataItem;
                if (!dataItem || !dataItem.dataContext) return text;
                const ctx = dataItem.dataContext;
                const percent = ctx.percent ? ` (${ctx.percent})` : "";
                return `${ctx.key}${percent}`;
            });
            series.slices.template.adapter.add("fill", (fill, target) => {
                return am4core.color(target.dataItem.dataContext.color);
            });

        } else if (data.type === "line") {
            chart = am4core.create(chartRef.current, am4charts.XYChart);
            chart.paddingRight = 20;
            chart.cursor = new am4charts.XYCursor(); // Enable cursor for hover


            let categoryAxis = chart.xAxes.push(new am4charts.CategoryAxis());
            categoryAxis.dataFields.category = "key";
            if(data.keyAxisLabel){
                categoryAxis.title.text = data.keyAxisLabel; // Add X-axis label
            }

            let valueAxis = chart.yAxes.push(new am4charts.ValueAxis());
            if(data.valueAxisLabel){
                valueAxis.title.text = data.valueAxisLabel; // Add Y-axis label
            }

            let series = chart.series.push(new am4charts.LineSeries());
            series.dataFields.valueY = "value";
            series.dataFields.categoryX = "key";
            series.strokeWidth = 2;
            series.tooltipText = "{key}: [bold]{value}[/]";
            series.tooltip.getFillFromObject = false;
            series.tooltip.background.fill = am4core.color("#fff");
            series.tooltip.label.fill = am4core.color("#000");
            series.tooltip.hiddenState.properties.opacity = 1;

            series.bullets.push(new am4charts.CircleBullet());

            chart.data = data.graphData;

        } else if (data.type === "multiline") {
            // Multiline Chart (Multiple line series)
            chart = am4core.create(chartRef.current, am4charts.XYChart);
            chart.paddingRight = 20;
            chart.cursor = new am4charts.XYCursor();

            let categoryAxis = chart.xAxes.push(new am4charts.CategoryAxis());
            categoryAxis.dataFields.category = "key";
            if (data.keyAxisLabel) {
                categoryAxis.title.text = data.keyAxisLabel;
            }

            let valueAxis = chart.yAxes.push(new am4charts.ValueAxis());
            if (data.valueAxisLabel) {
                valueAxis.title.text = data.valueAxisLabel;
            }

            // Restructure data so that each key has both datasets' values
            let combinedData = {};
            data.graphData.forEach(dataset => {
                dataset.data.forEach(point => {
                    if (!combinedData[point.key]) {
                        combinedData[point.key] = { key: point.key };
                    }
                    combinedData[point.key][dataset.name] = point.value;
                });
            });

            // Convert object to array
            chart.data = Object.values(combinedData);

            // Create a series for each dataset
            data.graphData.forEach(dataset => {
                let series = chart.series.push(new am4charts.LineSeries());
                series.dataFields.valueY = dataset.name;  // Use dataset name as field
                series.dataFields.categoryX = "key";
                series.name = dataset.name;
                series.strokeWidth = 2;
                series.tooltipText = "{name}: {valueY}";
                series.tooltip.getFillFromObject = false;
                series.tooltip.background.fill = am4core.color("#fff");
                series.tooltip.label.fill = am4core.color("#000");
                series.tooltip.hiddenState.properties.opacity = 1;
                
                if(dataset?.data[0]?.color)
                    series.stroke = am4core.color(dataset.data[0].color); // Assign dataset color
                series.bullets.push(new am4charts.CircleBullet());
            });
        
            // Enable legend to differentiate datasets
            chart.legend = new am4charts.Legend();
        }

        
        chartRef.current.chart = chart;

        return () => {
            chart.dispose();
        };
    }, [data]);

    const downloadExcel = () => {
        const keyHeader = data.keyAxisLabel || 'key';
        const wb = XLSX.utils.book_new();

        if (data.type === 'multiline') {
        // 1. Re-structure like in the chart
        const combined = {};
        data.graphData.forEach(dataset => {
            dataset.data.forEach(point => {
            if (!combined[point.key]) combined[point.key] = { key: point.key };
            combined[point.key][dataset.name] = point.value;
            });
        });

        // 2. Build rows with one column per series
        const sheetData = Object.values(combined).map(item => {
            const row = { [keyHeader]: item.key };
            data.graphData.forEach(dataset => {
            row[dataset.name] = item[dataset.name];
            });
            return row;
        });

        // 3. Export
        const sheet = XLSX.utils.json_to_sheet(sheetData);
        XLSX.utils.book_append_sheet(wb, sheet, 'Sheet1');
        XLSX.writeFile(wb, `${data.title || 'graph'}_${index}.xlsx`);

        } else {
        // single-series case
        const valHeader = data.valueAxisLabel || 'value';
        const sheetData = data.graphData.map(row => ({
            [keyHeader]: row.key,
            [valHeader]: row.value
        }));

        const sheet = XLSX.utils.json_to_sheet(sheetData);
        XLSX.utils.book_append_sheet(wb, sheet, 'Sheet1');
        XLSX.writeFile(wb, `${data.title || 'graph'}_${index}.xlsx`);
        }
      };

      const downloadMegaExcel = () => {
        const keyLabel = data?.keyAxisLabel;
        const valLabel = data?.valueAxisLabel;
        if (!keyLabel || !valLabel) return;
      
        const blocks = Array.isArray(allBlocks) ? allBlocks : [];
        const rows = [];
      
        blocks.forEach((blk) => {
          const blkGraphs = Array.isArray(blk?.graphs) ? blk.graphs : [];
      
          blkGraphs.forEach((g) => {
            if (!g?.keyAxisLabel || !g?.valueAxisLabel) return;
            if (g.keyAxisLabel !== keyLabel) return;
            if (g.valueAxisLabel !== valLabel) return;
      
            if (g.type === "multiline" && Array.isArray(g.graphData)) {
              g.graphData.forEach((dataset) => {
                const pts = Array.isArray(dataset?.data) ? dataset.data : [];
                pts.forEach((pt) => {
                  rows.push({
                    BlockID: blk.id,
                    Series: dataset?.name || "",
                    [keyLabel]: pt?.key,
                    [valLabel]: pt?.value,
                  });
                });
              });
              return;
            }
      
            const gd = Array.isArray(g.graphData) ? g.graphData : [];
            gd.forEach((r) => {
              rows.push({
                BlockID: blk.id,
                [keyLabel]: r?.key,
                [valLabel]: r?.value,
              });
            });
          });
        });
      
        if (!rows.length) return;
      
        const wb = XLSX.utils.book_new();
        const sheet = XLSX.utils.json_to_sheet(rows);
        XLSX.utils.book_append_sheet(wb, sheet, "Sheet1");
      
        const safe = (s) => String(s).replace(/[^\w\-]+/g, "_");
        XLSX.writeFile(wb, `mega_${safe(keyLabel)}_${safe(valLabel)}.xlsx`);
      };

    return (
        <div
            style={{
                marginBottom: "20px",
                width: "100%",
                border: "0px solid #444",
                padding: "10px",
                borderRadius: "8px",
                backgroundColor: "#2C2C2C",
                color: "#FFF"
            }}
            >
            <div style={{ display: "flex", justifyContent: "space-between" }}>
                <div>
                <h3 style={{ color: "#FFF" }}>{data.title}</h3>
                <p style={{ fontSize: 14, color: "#ccc" }}>{data.displayText}</p>
                </div>

                {/* action buttons */}
                <div style={{ display: "flex", alignItems: "center", gap: 8, top: 0 }}>
                {headerContent}
                <button
                onClick={downloadMegaExcel}
                title="Mega download"
                style={{ 
                    backgroundColor: '#2C2C2C',
                    color: '#FFF',
                    border: 'none',
                    borderRadius: '4px',
                    padding: '5px 8px',
                    cursor: 'pointer',
                    fontSize: '12px',
                    lineHeight: '1'
                }}
                disabled={!data?.keyAxisLabel || !data?.valueAxisLabel}
                >
                ⬇⬇
                </button>

                <button
                    onClick={downloadExcel}
                    title="Download Excel"
                    style={{ 
                        backgroundColor: '#2C2C2C',
                        color: '#FFF',
                        border: 'none',
                        borderRadius: '4px',
                        padding: '5px 5px',
                        cursor: 'pointer',
                        fontSize: '16px',
                        lineHeight: '1'
                        }}
                    >
                    ⬇
                </button>

                {onDelete && (
                    <button
                    onClick={onDelete}
                    title="Delete graph"
                    style={{ 
                        backgroundColor: '#2C2C2C',
                        color: '#FFF',
                        border: 'none',
                        borderRadius: '4px',
                        padding: '5px 5px',
                        cursor: 'pointer',
                        fontSize: '16px',
                        lineHeight: '1'
                        }}
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" fill="#FFFFFF" height="20" width="20" viewBox="0 0 20 20">
                            <path d="M0 0h24v24H0z" fill="none"/>
                            <path d="M16 9v10H8V9h8m-1.5-6h-5l-1 1H5v2h14V4h-4.5l-1-1z"/>
                        </svg>
                    </button>
                )}
                </div>

                
            </div>
            

            <div ref={chartRef} style={{ width: "100%", height: "250px" }} />
        </div>
    );
};

export default GraphDisplay;
